import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import currencySymbols from 'constants/currencies';
import { getClassOfServiceColor } from 'utils/classOfService';
import { useIsMobile } from 'hooks/useBreakpoint';

import { productFamilyCodes } from 'types/productFamilyCodes';
import Sidebar from 'components/ui/Sidebar';
import { journeyFareShape, passengersShape, bookingShape } from 'constants/propTypes';
import classesOfService from 'constants/propTypes/classesOfService';
import serviceTypeCodes from 'constants/serviceTypeCodes';

import useTrainDisruptionsQuery from 'components/queries/useTrainDisruptionsQuery';
import { toTravelDate } from 'utils/trainDisruptions';

import SelectFlexibility from '../SelectFlexibility';

import SearchResultsTable from './SearchResultsTable';
import CollapsibleJourneyHeader from './CollapsibleJourneyHeader';
import MobileJourneySection from './MobileJourneySection';
import { ResultsContainer, SearchResultsTableContainer, CollapsibleContent } from './JourneySearchTables.styles';
import {
  shouldDisplayMultipleFares,
  filterJourneys,
  runValidator,
  allSegmentsCannotBeRebooked,
  getFaresPerClass,
  getLowestFarePrice,
  hideLowestFares,
  exchangingAllLegsOfBooking
} from './SearchResultsTable/SearchResultsTable.utils';

const getCurrencySymbol = (currencyCode) => currencySymbols[currencyCode?.toUpperCase()] || currencyCode;

const JourneySearchTables = ({
  outboundDate,
  inboundDate,
  outboundJourneys,
  inboundJourneys,
  selectFare,
  selectedInbound,
  selectedOutbound,
  currency,
  isGroupBooking,
  isFlowForward,
  showFareDifference,
  originalOutboundTotal,
  originalInboundTotal,
  isWheelchairSearch,
  isEvacSearch,
  isUnassignedSearch,
  requestedProductFamilies,
  isExchange,
  outboundInfo,
  inboundInfo,
  isMember = false,
  shouldForceOutbound,
  shouldForceInbound,
  isHomogeneous = true,
  getOutboundSummatedFareExchangeCondition,
  getInboundSummatedFareExchangeCondition,
  isExchangingASubsetOfPassengersOutbound,
  isExchangingASubsetOfPassengersInbound,
  shouldUseSelection = false,
  passengers = [],
  isMultiJourneyBooking = false,
  isSummatedFaresSelectionEnabled = false,
  isExchangingMultipleLegsOfAOutboundSummated = false,
  isExchangingMultipleLegsOfAInboundSummated = false,
  isExchangingSinglegOfAOutboundSummated = false,
  isExchangingSingleLegOfAInboundSummated = false,
  outboundLegsBeingExchanged,
  inboundLegsBeingExchanged,
  isExchangingInboundDirectFare,
  isExchangingOutboundDirectFare,
  isExchangingOutboundThroughFare,
  isExchangingInboundThroughFare,
  booking,
  shouldOverridePrice = false,
  outboundClassOfServiceToExchange,
  inboundClassOfServiceToExchange,
  animalsPerTrain,
  animalsPerTrainLoading,
  assistancePerTrain,
  assistancePerTrainLoading,
  dogOnlySearch,
  serviceDetailsData = [],
  serviceDetailsLoading = { outbound: false, inbound: false },
  outboundClassesOfService = [],
  inboundClassesOfService = [],
  selectedProductFamily,
  existingOutbound,
  existingInbound,
  isEsUser,
  outboundExtras = [],
  inboundExtras = [],
  cheapestFares,
  lowestFareSearch,
  subscriptionStartDate,
  subscriptionEndDate,
  swapStations = false,
  isExchangingSingleOutboundLegOfSNCBSummated = false,
  isExchangingSingleInboundLegOfSNCBSummated = false
}) => {
  const history = useHistory();
  const selectedItemRefsUrl = new URLSearchParams(history.location.search).get('bookingItems');
  const selectedItemRefs = selectedItemRefsUrl?.split(',');
  const [isSelectionOpen, setSelectionOpen] = useState(false);
  const [selectedFare, setSelectedFare] = useState(null);
  const [isOutboundExpanded, setIsOutboundExpanded] = useState(true);
  const [isInboundExpanded, setIsInboundExpanded] = useState(true);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const isMobile = useIsMobile();
  const isExchangingAllLegsOfBooking = exchangingAllLegsOfBooking({ booking, outboundLegsBeingExchanged, inboundLegsBeingExchanged });

  const trainDisruptions = useTrainDisruptionsQuery({ outboundDate, inboundDate, outboundInfo, inboundInfo });

  const filterEROnly = (journey) =>
    journey?.fares?.some((fare) => fare?.legs?.some((leg) => leg?.serviceType?.code === serviceTypeCodes.GREENSPEED));

  const outboundJourneyToUse = filterJourneys(
    isExchange,
    isMultiJourneyBooking,
    getOutboundSummatedFareExchangeCondition,
    isExchangingASubsetOfPassengersOutbound,
    filterEROnly,
    outboundJourneys,
    isSummatedFaresSelectionEnabled,
    isExchangingAllLegsOfBooking,
    outboundClassesOfService,
    selectedProductFamily,
    outboundLegsBeingExchanged
  );

  const inboundJourneysToUse = filterJourneys(
    isExchange,
    isMultiJourneyBooking,
    getInboundSummatedFareExchangeCondition,
    isExchangingASubsetOfPassengersInbound,
    filterEROnly,
    inboundJourneys,
    isSummatedFaresSelectionEnabled,
    isExchangingAllLegsOfBooking,
    inboundClassesOfService,
    selectedProductFamily,
    inboundLegsBeingExchanged
  );

  const handleCloseSelection = () => setSelectionOpen(false);

  const handleSelectFare = (fare, journey) => {
    if (shouldUseSelection) {
      if (shouldDisplayMultipleFares(fare, journey)) {
        setSelectionOpen(true);
        setSelectedFare(fare);
        setSelectedJourney(journey);
      } else {
        selectFare(fare);
      }
    } else {
      selectFare(fare);
    }
  };

  const isLoyaltyBooking = requestedProductFamilies?.some((pf) => pf === productFamilyCodes.EUROSTAR_LOYALTY);

  const handleOutboundJourney = (fare, journey) => {
    handleSelectFare({ ...fare, direction: 'outbound' }, journey);

    // On mobile with return journey, collapse outbound and expand inbound
    if (isMobile && inboundDate) {
      setTimeout(() => {
        setIsOutboundExpanded(false);
        setIsInboundExpanded(true);
      }, 100);
    }
  };

  const handleInboundJourney = (fare, journey) => {
    handleSelectFare({ ...fare, direction: 'inbound' }, journey);

    // On mobile, collapse inbound after selection
    if (isMobile) {
      setTimeout(() => {
        setIsInboundExpanded(false);
      }, 100);
    }
  };

  const exchangedOutboundClassesOfService = (outboundLegsBeingExchanged ?? []).reduce((acc, { serviceType, classOfService }) => {
    const brandCode = serviceType?.brandCode;

    return {
      ...acc,
      [brandCode]: classOfService?.code
    };
  }, {});

  const exchangedInboundClassesOfService = (inboundLegsBeingExchanged ?? []).reduce((acc, { serviceType, classOfService }) => {
    const brandCode = serviceType?.brandCode;

    return {
      ...acc,
      [brandCode]: classOfService?.code
    };
  }, {});

  const lowestFares = {
    outbound: outboundClassesOfService.reduce((fares, { code }) => {
      if (
        hideLowestFares({
          isExchange,
          isSummatedEnabled: isSummatedFaresSelectionEnabled,
          isSingleSummated: isExchangingSinglegOfAOutboundSummated,
          isMultipleSummated: isExchangingMultipleLegsOfAOutboundSummated,
          isThroughFare: isExchangingOutboundThroughFare,
          legsBeingExchanged: outboundLegsBeingExchanged
        })
      ) {
        return { ...fares, [code]: -1 };
      }

      const faresPerClass = getFaresPerClass({
        journeys: outboundJourneyToUse,
        classOfService: code,
        isExchange
      });

      return {
        ...fares,
        [code]: getLowestFarePrice({
          fares: faresPerClass,
          isLoyalty: isLoyaltyBooking,
          isFlowForward,
          isGroup: isGroupBooking,
          isExchange,
          exchangedClassesOfService: exchangedOutboundClassesOfService
        })
      };
    }, {}),
    inbound: inboundClassesOfService.reduce((fares, { code }) => {
      if (
        hideLowestFares({
          isExchange,
          isSummatedEnabled: isSummatedFaresSelectionEnabled,
          isSingleSummated: isExchangingSingleLegOfAInboundSummated,
          isMultipleSummated: isExchangingMultipleLegsOfAInboundSummated,
          isThroughFare: isExchangingInboundThroughFare,
          legsBeingExchanged: inboundLegsBeingExchanged
        })
      ) {
        return { ...fares, [code]: -1 };
      }

      const faresPerClass = getFaresPerClass({
        journeys: inboundJourneysToUse,
        classOfService: code,
        isExchange
      });

      return {
        ...fares,
        [code]: getLowestFarePrice({
          fares: faresPerClass,
          isLoyalty: isLoyaltyBooking,
          isFlowForward,
          isGroup: isGroupBooking,
          isExchange,
          exchangedClassesOfService: exchangedInboundClassesOfService
        })
      };
    }, {})
  };

  const isOverridingRules =
    isExchange && selectedItemRefs?.length > 0 ? runValidator(booking, selectedItemRefs)([allSegmentsCannotBeRebooked]) : false;

  if (serviceDetailsLoading.outbound || serviceDetailsLoading.inbound) {
    return (
      <Row height="50vh">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  return (
    <ResultsContainer id="search-results-table">
      {outboundDate && outboundJourneyToUse && (
        <SearchResultsTableContainer>
          <CollapsibleJourneyHeader
            isExpanded={isOutboundExpanded}
            onToggle={() => setIsOutboundExpanded(!isOutboundExpanded)}
            direction="outbound"
            date={outboundDate}
            selectedFare={selectedOutbound}
            existingTicket={existingOutbound}
            currency={currency}
            getCurrencySymbol={getCurrencySymbol}
            getClassOfServiceColor={getClassOfServiceColor}
          />
          <CollapsibleContent isExpanded={isOutboundExpanded}>
            <MobileJourneySection
              direction="outbound"
              date={outboundDate}
              journeyInfo={outboundInfo}
              booking={booking}
              outboundDate={outboundDate}
              inboundDate={inboundDate}
              cheapestFares={cheapestFares?.[0]}
              currency={currency}
              subscriptionStartDate={subscriptionStartDate}
              subscriptionEndDate={subscriptionEndDate}
              isExchangingSingleLegOfSNCBSummated={isExchangingSingleOutboundLegOfSNCBSummated}
              swapStations={swapStations}
              shouldForce={shouldForceOutbound}
              totalJourneys={outboundJourneys.length}
              filteredJourneys={outboundJourneyToUse.length}
            />
            <SearchResultsTable
              id="outbound-search-results-table"
              lowestFareSearch={lowestFareSearch?.outbound}
              isOverridingRules={isOverridingRules}
              selectFare={handleOutboundJourney}
              selected={selectedOutbound}
              journeys={outboundJourneyToUse}
              currency={currency}
              isGroupBooking={isGroupBooking}
              isFlowForward={isFlowForward}
              originalTotalPrice={originalOutboundTotal}
              showFareDifference={showFareDifference}
              isWheelchairSearch={isWheelchairSearch}
              isEvacSearch={isEvacSearch}
              isUnassignedSearch={isUnassignedSearch}
              requestedProductFamilies={requestedProductFamilies}
              isExchange={isExchange}
              origin={outboundInfo?.originUIC}
              destination={outboundInfo?.destinationUIC}
              startDate={toTravelDate(outboundDate)}
              lowestFares={lowestFares.outbound}
              isLoyaltyBooking={isLoyaltyBooking}
              isMember={isMember}
              isExchangingMultipleLegsSummated={isExchangingMultipleLegsOfAOutboundSummated}
              isExchangingSingleLegOfASummated={isExchangingSinglegOfAOutboundSummated}
              isExchangingADirect={isExchangingOutboundDirectFare}
              isSummatedFareSelectionEnabled={isSummatedFaresSelectionEnabled}
              legsBeingExchanged={outboundLegsBeingExchanged}
              isExchangingAThroughFare={isExchangingOutboundThroughFare}
              shouldOverridePrice={shouldOverridePrice}
              classOfServiceToExchange={outboundClassOfServiceToExchange}
              animalsPerTrain={animalsPerTrain?.outbound}
              animalsPerTrainLoading={animalsPerTrainLoading?.outbound}
              assistancePerTrain={assistancePerTrain?.outbound}
              assistancePerTrainLoading={assistancePerTrainLoading?.outbound}
              serviceDetailsData={serviceDetailsData?.outbound}
              dogOnlySearch={dogOnlySearch}
              classesOfService={outboundClassesOfService}
              exchangedClassesOfService={exchangedOutboundClassesOfService}
              isEsUser={isEsUser}
              extras={outboundExtras}
              trainDisruptions={trainDisruptions.outbound}
              passengers={passengers}
            />
          </CollapsibleContent>
        </SearchResultsTableContainer>
      )}
      {inboundDate && inboundJourneysToUse && (
        <SearchResultsTableContainer>
          <CollapsibleJourneyHeader
            isExpanded={isInboundExpanded}
            onToggle={() => setIsInboundExpanded(!isInboundExpanded)}
            direction="inbound"
            date={inboundDate}
            selectedFare={selectedInbound}
            existingTicket={existingInbound}
            currency={currency}
            getCurrencySymbol={getCurrencySymbol}
            getClassOfServiceColor={getClassOfServiceColor}
          />
          <CollapsibleContent isExpanded={isInboundExpanded}>
            <MobileJourneySection
              direction="inbound"
              date={inboundDate}
              journeyInfo={inboundInfo}
              booking={booking}
              outboundDate={outboundDate}
              inboundDate={inboundDate}
              cheapestFares={cheapestFares?.[1]}
              currency={currency}
              subscriptionStartDate={subscriptionStartDate}
              subscriptionEndDate={subscriptionEndDate}
              isExchangingSingleLegOfSNCBSummated={isExchangingSingleInboundLegOfSNCBSummated}
              swapStations={swapStations}
              shouldForce={shouldForceInbound}
              totalJourneys={inboundJourneys.length}
              filteredJourneys={inboundJourneysToUse.length}
            />
            <SearchResultsTable
              id="inbound-search-results-table"
              lowestFareSearch={lowestFareSearch?.inbound}
              isOverridingRules={isOverridingRules}
              selectFare={handleInboundJourney}
              selected={selectedInbound}
              journeys={inboundJourneysToUse}
              currency={currency}
              isGroupBooking={isGroupBooking}
              isFlowForward={isFlowForward}
              originalTotalPrice={originalInboundTotal}
              showFareDifference={showFareDifference}
              isWheelchairSearch={isWheelchairSearch}
              isEvacSearch={isEvacSearch}
              isUnassignedSearch={isUnassignedSearch}
              requestedProductFamilies={requestedProductFamilies}
              isExchange={isExchange}
              outboundOrigin={outboundInfo?.originUIC}
              outboundDestination={outboundInfo?.destinationUIC}
              origin={inboundInfo?.originUIC}
              destination={inboundInfo?.destinationUIC}
              startDate={toTravelDate(inboundDate)}
              lowestFares={lowestFares.inbound}
              isLoyaltyBooking={isLoyaltyBooking}
              isMember={isMember}
              isExchangingMultipleLegsSummated={isExchangingMultipleLegsOfAInboundSummated}
              isExchangingSingleLegOfASummated={isExchangingSingleLegOfAInboundSummated}
              isExchangingADirect={isExchangingInboundDirectFare}
              isSummatedFareSelectionEnabled={isSummatedFaresSelectionEnabled}
              legsBeingExchanged={inboundLegsBeingExchanged}
              isExchangingAThroughFare={isExchangingInboundThroughFare}
              shouldOverridePrice={shouldOverridePrice}
              classOfServiceToExchange={inboundClassOfServiceToExchange}
              animalsPerTrain={animalsPerTrain?.inbound}
              animalsPerTrainLoading={animalsPerTrainLoading?.inbound}
              assistancePerTrain={assistancePerTrain?.inbound}
              assistancePerTrainLoading={assistancePerTrainLoading?.inbound}
              serviceDetailsData={serviceDetailsData?.inbound}
              dogOnlySearch={dogOnlySearch}
              classesOfService={inboundClassesOfService}
              exchangedClassesOfService={exchangedInboundClassesOfService}
              isEsUser={isEsUser}
              extras={inboundExtras}
              trainDisruptions={trainDisruptions.inbound}
              passengers={passengers}
            />
          </CollapsibleContent>
        </SearchResultsTableContainer>
      )}
      <Sidebar isOpen={isSelectionOpen} onClose={handleCloseSelection}>
        <SelectFlexibility
          selectedFare={selectedFare}
          selectedJourney={selectedJourney}
          selectFare={selectFare}
          selectedOutbound={selectedOutbound}
          selectedInbound={selectedInbound}
          currency={currency}
          isLoyaltyBooking={isLoyaltyBooking}
          isWheelchairSearch={isWheelchairSearch}
          isEvacSearch={isEvacSearch}
          isUnassignedSearch={isUnassignedSearch}
          isGroupBooking={isGroupBooking}
          isExchange={isExchange}
          isFlowForward={isFlowForward}
          passengers={passengers}
          lowestFares={lowestFares}
          outboundInfo={outboundInfo}
          inboundInfo={inboundInfo}
          classesOfService={{
            outbound: outboundClassesOfService,
            inbound: inboundClassesOfService
          }}
          exchangedClassesOfService={{
            outbound: exchangedOutboundClassesOfService,
            inbound: exchangedInboundClassesOfService
          }}
        />
      </Sidebar>
    </ResultsContainer>
  );
};

JourneySearchTables.propTypes = {
  lowestFareSearch: PropTypes.object,
  outboundDate: PropTypes.string,
  inboundDate: PropTypes.string,
  outboundJourneys: PropTypes.arrayOf(PropTypes.object),
  inboundJourneys: PropTypes.arrayOf(PropTypes.object),
  selectedOutbound: journeyFareShape,
  selectedInbound: journeyFareShape,
  currency: PropTypes.string,
  selectFare: PropTypes.func,
  isGroupBooking: PropTypes.bool,
  isFlowForward: PropTypes.bool,
  showFareDifference: PropTypes.bool,
  isWheelchairSearch: PropTypes.bool,
  isEvacSearch: PropTypes.bool,
  isUnassignedSearch: PropTypes.bool,
  originalOutboundTotal: PropTypes.number,
  originalInboundTotal: PropTypes.number,
  requestedProductFamilies: PropTypes.arrayOf(PropTypes.string),
  isMultiJourneyBooking: PropTypes.bool,
  isExchange: PropTypes.bool,
  isMember: PropTypes.bool,
  shouldForceOutbound: PropTypes.bool,
  shouldForceInbound: PropTypes.bool,
  shouldUseSelection: PropTypes.bool,
  passengers: passengersShape,
  isHomogeneous: PropTypes.bool,
  getOutboundSummatedFareExchangeCondition: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  getInboundSummatedFareExchangeCondition: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isExchangingASubsetOfPassengersOutbound: PropTypes.bool,
  isExchangingASubsetOfPassengersInbound: PropTypes.bool,
  isExchangingMultipleLegsOfAInboundSummated: PropTypes.bool,
  outboundLegsBeingExchanged: PropTypes.arrayOf(PropTypes.object),
  inboundLegsBeingExchanged: PropTypes.arrayOf(PropTypes.object),
  isExchangingInboundDirectFare: PropTypes.bool,
  isExchangingOutboundDirectFare: PropTypes.bool,
  isExchangingOutboundThroughFare: PropTypes.bool,
  isExchangingInboundThroughFare: PropTypes.bool,
  isExchangingMultipleLegsOfAOutboundSummated: PropTypes.bool,
  isExchangingSinglegOfAOutboundSummated: PropTypes.bool,
  isExchangingSingleLegOfAInboundSummated: PropTypes.bool,
  isSummatedFaresSelectionEnabled: PropTypes.bool,
  outboundInfo: PropTypes.shape({
    date: PropTypes.string,
    origin: PropTypes.string,
    destination: PropTypes.string,
    originUIC: PropTypes.string,
    destinationUIC: PropTypes.string
  }),
  inboundInfo: PropTypes.shape({
    date: PropTypes.string,
    origin: PropTypes.string,
    destination: PropTypes.string,
    originUIC: PropTypes.string,
    destinationUIC: PropTypes.string
  }),
  booking: bookingShape,
  shouldOverridePrice: PropTypes.bool,
  outboundClassOfServiceToExchange: PropTypes.string,
  inboundClassOfServiceToExchange: PropTypes.string,
  dogOnlySearch: PropTypes.bool,
  animalsPerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  animalsPerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  assistancePerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  assistancePerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  outboundClassesOfService: classesOfService,
  inboundClassesOfService: classesOfService,
  serviceDetailsData: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  serviceDetailsLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  selectedProductFamily: PropTypes.string,
  isEsUser: PropTypes.bool,
  existingOutbound: PropTypes.object,
  existingInbound: PropTypes.object,
  outboundExtras: PropTypes.arrayOf(PropTypes.object),
  inboundExtras: PropTypes.arrayOf(PropTypes.object),
  cheapestFares: PropTypes.arrayOf(
    PropTypes.shape({
      cheapestFares: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          price: PropTypes.number
        })
      )
    })
  ),
  subscriptionStartDate: PropTypes.string,
  subscriptionEndDate: PropTypes.string,
  swapStations: PropTypes.bool,
  isExchangingSingleOutboundLegOfSNCBSummated: PropTypes.bool,
  isExchangingSingleInboundLegOfSNCBSummated: PropTypes.bool
};

export default JourneySearchTables;
